/*
  Guessing a column mapping from the headers of a client HR extract.

  Ported from the single-file app: `COLUMN_KEYS[].patterns` (orgchart.html:1812), `autoMapColumns`
  (:2752), `confidenceLabel` (:2775), `seedDefaultLevels` (:2860) and `detectNumeric` (:2842).

  WHY THIS EXISTS NOW, HAVING DELIBERATELY NOT BEEN PORTED BEFORE. `fields.ts` used to carry the
  reasoning for leaving the patterns behind: the platform never guessed, because it only ever
  ingested workbooks the app itself had exported, and those already carry a mapping a human
  confirmed. A second, quieter opinion about what a column means would have been all cost.

  That held exactly as long as a project could only be born from an export — which meant every
  engagement had to be opened in the frozen app first, mapped there, and exported before the
  platform could see it. The platform now ingests a plain roster, so there is a first mapping with
  nobody to have confirmed it, and guessing well is the difference between a usable import and a
  screen of `(unnamed)` rows.

  THE GUESS IS A STARTING POINT, NOT AN ANSWER. Everything here is offered to the user with its
  confidence shown, and every field stays editable afterwards through `setColumnMapping` —
  `mappingConfidence` exists precisely so a low-confidence guess advertises itself rather than
  hiding among the ones that are right.

  PURE, and deliberately so: these take headers and rows and return values. That is what lets the
  server derive a mapping at import time and the client re-derive the same labels for the editor
  without a round trip.
*/
import { COLUMN_FIELDS } from './fields.js';
/**
 * Header spellings each logical field answers to, verbatim from the app's `COLUMN_KEYS`.
 *
 * Order within a list does not matter — scoring compares every pattern and keeps the best — but the
 * order of the KEYS does: `autoMapColumns` assigns greedily and a column already taken is skipped,
 * so an earlier field wins a contested header. `name` and `id` come first for that reason.
 */
export const COLUMN_PATTERNS = {
    name: ['name', 'full name', 'employee name', 'fullname', 'full_name'],
    id: ['id', 'personnel id', 'personnel_id', 'employee id', 'emp id', 'staff id', 'empid'],
    supervisorId: [
        'supervisor id', 'manager id', 'reports to', 'supervisor', 'manager', 'line manager',
        'mgr id', 'reportsto', 'managerid', 'supervisorid', 'reports to id',
    ],
    fte: ['fte', 'full time equivalent', 'full-time equivalent', 'fte ratio'],
    location: ['location', 'office', 'site', 'city'],
    jobTitle: ['title', 'job title', 'job_title', 'position', 'role'],
    businessUnit: ['business unit', 'bu', 'segment', 'market', 'business_unit'],
    legalEntity: ['legal entity', 'legalentity', 'legal_entity', 'legal entity name', 'entity', 'company code'],
    country: ['country', 'country name', 'nation', 'ctry'],
    salary: ['salary', 'comp', 'compensation', 'pay', 'annual salary', 'cost', 'total comp', 'base'],
    seniority: ['seniority', 'level', 'grade', 'band', 'rank'],
    openPosition: [
        'open position', 'open role', 'open req', 'vacant', 'vacancy', 'unfilled',
        'position status', 'requisition', 'tbh', 'to be hired',
    ],
    inactive: [
        'inactive', 'on leave', 'loa', 'leave of absence', 'suspended', 'dormant',
        'absence', 'employment status',
    ],
    dedication: [
        'dedication', 'dedication %', 'allocation', 'allocated', 'carve-out %', 'carve out %',
        'carveout share', 'carve-out share', 'degree of dedication', 'split head', 'split',
        'shared service', 'shared', 'dual', 'both', 'split role',
    ],
    currency: [
        'currency', 'currency code', 'ccy', 'salary currency', 'pay currency', 'iso currency',
        'währung', 'waehrung',
    ],
    pensionScheme: [
        'pension', 'pension scheme', 'pension plan', 'pension type', 'retirement plan', 'db/dc',
        'pension fund', 'altersvorsorge',
    ],
};
/** Whitespace, underscores, hyphens and dots carry no meaning in a header. */
function norm(value) {
    return String(value ?? '').toLowerCase().replace(/[\s_\-.]/g, '');
}
/**
 * How well one header answers to one field: 100 exact, 80 prefix or suffix, 60 substring, 0 none.
 *
 * The three tiers are the app's and are kept rather than tidied into a distance metric. They are
 * what make "Employee ID" beat "ID Card" for `id`, and a change here would silently re-map columns
 * on files that import correctly today.
 */
export function headerScore(header, key) {
    const h = norm(header);
    let best = 0;
    for (const pattern of COLUMN_PATTERNS[key]) {
        const p = norm(pattern);
        if (!p)
            continue;
        if (h === p)
            best = Math.max(best, 100);
        else if (h.startsWith(p) || h.endsWith(p))
            best = Math.max(best, 80);
        else if (h.includes(p))
            best = Math.max(best, 60);
    }
    return best;
}
/** Below this a match is noise — the app's threshold, and the line between a guess and a shrug. */
export const MIN_CONFIDENT_SCORE = 60;
/**
 * A best-effort `columnMap` for a set of headers.
 *
 * GREEDY AND UNIQUE, per the original: each field takes its best unclaimed header, and a header
 * already spoken for is not offered again. Two fields therefore cannot both read one column, which
 * is the same rule `setColumnMapping` enforces at the command layer — so a suggestion is never
 * refused by the very guard it has to pass.
 */
export function suggestColumnMap(headers) {
    const map = {};
    const taken = new Set();
    for (const field of COLUMN_FIELDS) {
        let best = null;
        let bestScore = 0;
        for (const header of headers) {
            if (taken.has(header))
                continue;
            const score = headerScore(header, field.key);
            if (score > bestScore) {
                bestScore = score;
                best = header;
            }
        }
        if (best !== null && bestScore >= MIN_CONFIDENT_SCORE) {
            map[field.key] = best;
            taken.add(best);
        }
    }
    return map;
}
/**
 * How much to trust one entry of a mapping — including one a person chose by hand.
 *
 * `'manual'` is not a failure: it is a column whose name says nothing, which is the normal state of
 * a deliberate choice. The distinction the UI needs is between "we guessed this and you should
 * look" and "you told us", and the score is what separates them.
 */
export function mappingConfidence(key, map) {
    const column = map[key];
    if (!column)
        return 'none';
    const score = headerScore(column, key);
    if (score >= 100)
        return 'high';
    if (score >= 80)
        return 'likely';
    if (score >= MIN_CONFIDENT_SCORE)
        return 'possible';
    return 'manual';
}
/**
 * Does this column read as a number? Sampled over the first 50 non-blank values, 90% threshold.
 *
 * Used only to keep a salary or an id out of the hierarchy when no named tier matched. Sampling
 * rather than scanning is the app's choice and it matters at roster scale.
 */
export function detectNumeric(rows, column) {
    let seen = 0;
    let numeric = 0;
    for (const row of rows) {
        const value = row[column];
        if (value === null || value === undefined || value === '')
            continue;
        seen++;
        if (Number.isFinite(Number(value)))
            numeric++;
        if (seen >= 50)
            break;
    }
    return seen > 0 && numeric / seen >= 0.9;
}
/** The tiers a hierarchy is usually spelled in, outermost first. */
const LEVEL_TIERS = [
    ['division', 'board', 'businessarea', 'area', 'group'],
    ['function', 'department', 'dept'],
    ['subfunction', 'subdepartment', 'subdept', 'team', 'subteam', 'unit'],
];
/**
 * The starting hierarchy: up to three levels picked from the columns no attribute field claimed.
 *
 * RETURNS BOTH HALVES, and the caller must write them together. `setGroupingLevels` refuses a level
 * whose key has no mapped column, and `setColumnMapping` refuses to orphan a declared level — so on
 * a fresh project neither can go first. Writing `groupingLevels` and `columnMap` in one transaction
 * at creation satisfies both by construction, which is why this returns a pair rather than mutating
 * a map the caller already holds.
 *
 * Falling back to the first non-numeric spare column is the app's behaviour and is deliberately
 * modest: one level a person can rename beats three the file did not ask for. An extract with
 * nothing spare gets no levels, and everybody hangs directly under the root — which the empty state
 * says out loud rather than looking broken.
 */
export function seedGroupingLevels(headers, columnMap, rows = []) {
    const used = new Set(Object.values(columnMap));
    const available = headers.filter((h) => !used.has(h));
    const picked = [];
    for (const tier of LEVEL_TIERS) {
        const hit = available.find((column) => !picked.includes(column) &&
            tier.some((pattern) => norm(column) === pattern || norm(column).includes(pattern)));
        if (hit)
            picked.push(hit);
    }
    if (picked.length === 0) {
        const text = available.find((column) => !detectNumeric(rows, column));
        if (text)
            picked.push(text);
    }
    const levels = [];
    const columns = {};
    picked.forEach((column, index) => {
        const key = `lvl${index + 1}`;
        levels.push({ key, label: `Level ${index + 1}` });
        columns[key] = column;
    });
    return { levels, columns };
}
//# sourceMappingURL=column-guess.js.map